// Assessment lifecycle band (assessment-viz Surface 1) — an always-visible, lifecycle-adaptive
// visualization near the top of the assessment page. Every variant is fed from REAL data; missing
// values render as "—" (never 0/fabricated), and nothing is shown for data that does not exist
// (no risk themes, no per-section/per-question review state — review is assessment-level).

import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { formatDate } from '@/lib/utils';
import { useAssessmentSectionScores } from '@/api/hooks/useAssessments';
import type { AssessmentDetail, FindingSeverity } from '@/api/types/assessment.types';

type Variant = 'draft' | 'in_progress' | 'under_review' | 'scored' | 'terminal';

function selectVariant(status: string, anyAnswered: boolean): Variant {
  switch (status) {
    case 'draft': return 'draft';
    case 'issued': return anyAnswered ? 'in_progress' : 'draft';
    case 'in_progress': return 'in_progress';
    case 'submitted':
    case 'under_review':
    case 'returned':
    case 'resubmitted':
    case 'reopened': return 'under_review';
    case 'approved': return 'scored';
    case 'rejected': return 'scored'; // surfaces the reject decision
    case 'cancelled':
    case 'expired': return 'terminal';
    default: return 'draft';
  }
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', issued: 'Issued', in_progress: 'In progress', submitted: 'Submitted',
  under_review: 'Under review', returned: 'Returned', resubmitted: 'Resubmitted',
  reopened: 'Reopened', approved: 'Approved', rejected: 'Rejected',
  cancelled: 'Cancelled', expired: 'Expired',
};

// Pipeline reached-index (draft→issued→in_progress→submitted→under_review→approved).
const PIPELINE = ['draft', 'issued', 'in_progress', 'submitted', 'under_review', 'approved'] as const;
const PHASE_INDEX: Record<string, number> = {
  draft: 0, issued: 1, in_progress: 2, submitted: 3,
  under_review: 4, returned: 4, resubmitted: 4, reopened: 4,
  approved: 5, rejected: 5, cancelled: 1, expired: 1,
};

const SEVERITY_TILES: { key: FindingSeverity; label: string; bar: string; fg: string; border: string }[] = [
  { key: 'critical', label: 'Critical', bar: 'bg-severity-critical', fg: 'text-severity-critical', border: 'border-severity-critical' },
  { key: 'high', label: 'High', bar: 'bg-severity-high', fg: 'text-severity-high', border: 'border-severity-high' },
  { key: 'medium', label: 'Medium', bar: 'bg-severity-medium', fg: 'text-severity-medium', border: 'border-severity-medium' },
  { key: 'low', label: 'Low', bar: 'bg-severity-low', fg: 'text-severity-low', border: 'border-severity-low' },
  { key: 'informational', label: 'Info', bar: 'bg-severity-info', fg: 'text-severity-info', border: 'border-severity-info' },
];

const SECTION_STATUS_BAR: Record<string, string> = {
  pass: 'bg-cert-valid-stripe', conditional: 'bg-cert-warn-stripe', fail: 'bg-cert-expired-stripe', pending: 'bg-severity-low',
};

// ── Small SVG ring (neutral progress / pass-fail score) ────────────────────────
function Ring({ pct, center, sub, stroke }: { pct: number; center: string; sub?: string; stroke: string }) {
  const r = 52, c = 2 * Math.PI * r;
  const dash = Math.max(0, Math.min(pct, 1)) * c;
  return (
    <svg width="128" height="128" viewBox="0 0 132 132" className="shrink-0" aria-hidden="true">
      <circle cx="66" cy="66" r={r} fill="none" stroke="#1B2C43" strokeWidth="14" />
      {dash > 0 && (
        <circle cx="66" cy="66" r={r} fill="none" stroke={stroke} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`} transform="rotate(-90 66 66)" />
      )}
      <text x="66" y={sub ? 62 : 71} textAnchor="middle" fill="#F8FAFC" fontSize="28" fontWeight="700" fontFamily="JetBrains Mono, monospace">{center}</text>
      {sub && <text x="66" y="82" textAnchor="middle" fill="#8DA0B8" fontSize="11" fontFamily="JetBrains Mono, monospace">{sub}</text>}
    </svg>
  );
}

const LABEL = 'text-[10.5px] font-bold uppercase tracking-wider text-text-secondary';

// Adaptive per-section list, present in every non-terminal variant. It shows ONLY real data:
// counts (structure), completion (answered/total — derived), and per-section flag counts. It never
// implies section-level scoring or review state pre-scoring (those don't exist). Scored uses its
// own performance list (from the section-scores endpoint).
type SecRow = { id: string; name: string; weight: number | null; answered: number; total: number; flagged: number; pct: number };
function SectionRows({ rows, mode }: { rows: SecRow[]; mode: 'counts' | 'progress' | 'review' }) {
  const title = mode === 'counts' ? 'Sections' : mode === 'review' ? 'Section completion' : 'Progress by section';
  const hint = mode === 'counts' ? 'weight · questions' : mode === 'review' ? 'answered · flagged' : 'weight · answered';
  return (
    <div className="flex-1 min-w-[280px] flex flex-col gap-2 max-h-[190px] overflow-y-auto pr-1.5">
      <div className="flex items-center justify-between"><span className={LABEL}>{title}</span><span className="text-[11px] text-text-secondary">{hint}</span></div>
      {rows.map((s) => (
        <div key={s.id} className="flex items-center gap-3">
          <span className="flex-[0_0_168px] text-xs text-text-primary truncate">{s.name}</span>
          <span className="flex-[0_0_34px] text-[10.5px] font-mono text-text-secondary text-right">{s.weight != null ? `${s.weight}%` : '—'}</span>
          {mode === 'counts' ? (
            <span className="flex-1 text-[11.5px] font-mono text-text-secondary text-right">{s.total} question{s.total !== 1 ? 's' : ''}</span>
          ) : (
            <>
              <span className="flex-1 h-2 rounded bg-cert-neutral-bg overflow-hidden">
                <span className={`block h-full rounded ${s.pct >= 1 ? 'bg-cert-valid-stripe' : s.pct >= 0.5 ? 'bg-severity-low' : s.pct > 0 ? 'bg-cert-warn-stripe' : ''}`} style={{ width: `${Math.round(s.pct * 100)}%` }} />
              </span>
              <span className={`flex-[0_0_46px] text-[11.5px] font-mono text-right ${s.answered === 0 ? 'text-text-faint' : 'text-text-muted'}`}>{s.answered}/{s.total}</span>
            </>
          )}
          {mode === 'review' && (
            <span className={`flex-[0_0_52px] text-[11px] font-mono text-right ${s.flagged > 0 ? 'text-severity-high' : 'text-text-faint'}`}>{s.flagged > 0 ? `${s.flagged} flag${s.flagged !== 1 ? 's' : ''}` : '—'}</span>
          )}
        </div>
      ))}
    </div>
  );
}

export function AssessmentLifecycleBand({ assessment }: { assessment: AssessmentDetail }) {
  const d = useMemo(() => {
    const allQ = assessment.sections.flatMap((s) => s.questions);
    const total = allQ.length;
    const answered = allQ.filter((q) => q.response).length;
    const required = allQ.filter((q) => q.is_required);
    const optional = allQ.filter((q) => !q.is_required);
    const bypassed = assessment.questions_bypassed_count ?? allQ.filter((q) => q.response?.is_cert_bypassed).length;
    const sev: Record<FindingSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0, informational: 0 };
    let flagged = 0;
    for (const q of allQ) {
      if (q.response?.is_flagged) {
        flagged++;
        const s = q.response.flag_severity;
        if (s && s in sev) sev[s]++;
      }
    }
    return {
      total, answered, sections: assessment.sections.length,
      requiredTotal: required.length, requiredAnswered: required.filter((q) => q.response).length,
      optionalTotal: optional.length, optionalAnswered: optional.filter((q) => q.response).length,
      bypassed, sev, flagged,
      sectionProgress: assessment.sections.map((s) => {
        const t = s.questions.length;
        const a = s.questions.filter((q) => q.response).length;
        const f = s.questions.filter((q) => q.response?.is_flagged).length;
        return { id: s.id, name: s.name, weight: s.weight_pct, answered: a, total: t, flagged: f, pct: t ? a / t : 0 };
      }),
    };
  }, [assessment]);

  const anyAnswered = d.answered > 0;
  const variant = selectVariant(assessment.status, anyAnswered);
  const threshold = assessment.pass_threshold_pct ?? 70;
  const scorePct = assessment.final_score;
  const outcome = assessment.outcome; // authoritative pass/conditional/fail (api-computed)
  const scoredDate = assessment.approved_at ?? assessment.reviewed_at;

  // S1-D: section performance only needed in the scored variant → lazy fetch (enabled gate).
  const { data: sectionScores } = useAssessmentSectionScores(assessment.id, variant === 'scored');

  const phase = PHASE_INDEX[assessment.status] ?? 0;
  const dot = variant === 'scored'
    ? (assessment.status === 'rejected' ? 'bg-cert-expired-stripe' : 'bg-cert-valid-stripe')
    : variant === 'under_review' ? 'bg-accent-ai'
    : variant === 'in_progress' ? 'bg-severity-low'
    : variant === 'terminal' ? 'bg-text-faint' : 'bg-severity-info';

  return (
    <Card>
      <CardContent className="p-5 min-h-[250px] flex flex-col gap-4">
        {/* Persistent top row */}
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
            <span className="text-sm font-semibold text-text-primary">{STATUS_LABEL[assessment.status] ?? assessment.status}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11.5px] font-mono text-text-secondary flex-wrap">
            {PIPELINE.map((p, i) => (
              <span key={p} className="flex items-center gap-1.5">
                <span className={i <= phase ? 'text-accent-sky' : 'text-text-faint'}>{p}</span>
                {i < PIPELINE.length - 1 && <span className="text-text-faint">→</span>}
              </span>
            ))}
          </div>
          <div className="ml-auto text-xs text-text-secondary">
            {assessment.template_name ?? assessment.title}
            {assessment.issued_at && <> · issued {formatDate(assessment.issued_at)}</>}
            {assessment.due_date && <> · due {formatDate(assessment.due_date)}</>}
          </div>
        </div>

        {/* ── DRAFT / EMPTY ── */}
        {variant === 'draft' && (
          <div className="flex gap-6 flex-wrap items-stretch">
            <div className="flex flex-col justify-center gap-3 pr-5 border-r border-border-subtle min-w-[210px]">
              <span className="text-sm font-semibold text-text-primary">No answers yet</span>
              <span className="text-xs text-text-secondary max-w-[34ch] leading-relaxed">Progress and section breakdowns appear here as the vendor responds.</span>
              <div className="flex gap-5 pt-1">
                {[[d.total, 'questions'], [d.sections, 'sections'], [d.requiredTotal, 'required']].map(([n, l]) => (
                  <div key={l as string} className="flex flex-col gap-0.5">
                    <span className="text-lg font-bold text-text-primary">{n as number}</span>
                    <span className="text-[11px] text-text-secondary">{l as string}</span>
                  </div>
                ))}
              </div>
            </div>
            <SectionRows rows={d.sectionProgress} mode="counts" />
          </div>
        )}

        {/* ── IN PROGRESS ── */}
        {variant === 'in_progress' && (
          <div className="flex gap-6 flex-wrap items-stretch">
            <div className="flex items-center gap-4 pr-5 border-r border-border-subtle">
              <Ring
                pct={d.total ? d.answered / d.total : 0}
                center={`${d.total ? Math.round((d.answered / d.total) * 100) : 0}%`}
                sub={`${d.answered} / ${d.total}`}
                stroke="#4FA8E8"
              />
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Required</span><span className="text-base font-semibold text-text-primary">{d.requiredAnswered} <span className="text-xs text-text-secondary font-normal">/ {d.requiredTotal} answered</span></span></div>
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Optional</span><span className="text-base font-semibold text-text-primary">{d.optionalAnswered} <span className="text-xs text-text-secondary font-normal">/ {d.optionalTotal} answered</span></span></div>
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Bypassed via certification</span><span className="text-base font-semibold text-cert-info-fg">{d.bypassed} <span className="text-xs text-text-secondary font-normal">questions</span></span></div>
              </div>
            </div>
            <SectionRows rows={d.sectionProgress} mode="progress" />
          </div>
        )}

        {/* ── UNDER REVIEW ── */}
        {variant === 'under_review' && (
          <div className="flex flex-col gap-4">
          <div className="flex gap-6 flex-wrap items-stretch">
            <div className="flex flex-col gap-3 pr-5 border-r border-border-subtle min-w-[230px]">
              <span className={LABEL}>Review status</span>
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center text-[11px] font-bold rounded-md border px-2.5 py-1 bg-accent-ai/15 text-accent-ai-soft border-accent-ai/40">{STATUS_LABEL[assessment.status]}</span>
                {assessment.submitted_at && <span className="text-xs text-text-secondary">since {formatDate(assessment.submitted_at)}</span>}
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Submitted</span><span className="text-sm text-text-primary">{d.answered} / {d.total} answered{d.answered === d.total ? ' · complete' : ''}</span></div>
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Bypassed via certification</span><span className="text-sm text-cert-info-fg">{d.bypassed} questions</span></div>
              </div>
              <p className="text-[11.5px] text-text-secondary leading-snug pt-0.5">Review status is assessment-level; sections and questions carry no review state.</p>
            </div>
            <div className="flex-1 min-w-[300px] flex flex-col gap-3">
              <div className="flex items-baseline justify-between"><span className={LABEL}>Flagged findings by severity</span><span className="text-xs text-text-primary"><strong>{d.flagged}</strong> <span className="text-text-secondary">flagged of {d.total}</span></span></div>
              {d.flagged > 0 ? (
                <>
                  <div className="flex h-3 rounded-md overflow-hidden bg-cert-neutral-bg">
                    {SEVERITY_TILES.map((t) => d.sev[t.key] > 0 && (
                      <span key={t.key} className={`block ${t.bar}`} style={{ width: `${(d.sev[t.key] / d.flagged) * 100}%` }} />
                    ))}
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {SEVERITY_TILES.map((t) => (
                      <div key={t.key} className={`bg-bg-surface-2 border border-border-subtle border-t-2 ${t.border} rounded-lg px-2.5 py-2.5 flex flex-col gap-0.5`}>
                        <span className={`text-xl font-bold ${t.fg}`}>{d.sev[t.key]}</span>
                        <span className="text-[11px] text-text-muted">{t.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-text-secondary">No questions have been flagged.</p>
              )}
            </div>
          </div>
          {/* Per-section completion + flags (no section-level score/review — those don't exist) */}
          <div className="pt-3 border-t border-border-subtle">
            <SectionRows rows={d.sectionProgress} mode="review" />
          </div>
          </div>
        )}

        {/* ── SCORED (approved / rejected) ── */}
        {variant === 'scored' && (
          <div className="flex gap-6 flex-wrap items-stretch">
            <div className="flex items-center gap-4 pr-5 border-r border-border-subtle">
              <Ring
                pct={scorePct != null ? scorePct / 100 : 0}
                center={scorePct != null ? `${Math.round(scorePct)}%` : '—'}
                sub={`threshold ${threshold}%`}
                stroke={outcome === 'pass' ? '#3FB980' : outcome === 'conditional' ? '#D7A94A' : '#C2453F'}
              />
              <div className="flex flex-col gap-3">
                <span className={`self-start inline-flex items-center text-[11px] font-bold rounded-md border px-2.5 py-1 ${
                  assessment.status === 'rejected' ? 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border'
                  : outcome === 'pass' ? 'bg-cert-valid-bg text-cert-valid-fg border-cert-valid-border'
                  : outcome === 'conditional' ? 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border'
                  : 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border'
                }`}>{
                  assessment.status === 'rejected' ? 'Rejected'
                  : outcome === 'pass' ? 'Passed'
                  : outcome === 'conditional' ? 'Conditional'
                  : outcome === 'fail' ? 'Did not pass'
                  : '—'
                }</span>
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Scored</span><span className="text-sm text-text-primary">{scoredDate ? formatDate(scoredDate) : '—'}</span></div>
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Bypassed via certification</span><span className="text-sm text-cert-info-fg">{d.bypassed} of {d.total} questions</span></div>
                <div className="flex flex-col gap-0.5"><span className={LABEL}>Findings</span><span className="text-sm text-text-primary">{d.flagged} flagged · {d.sev.critical} critical</span></div>
              </div>
            </div>
            <div className="flex-1 min-w-[300px] flex flex-col gap-2 max-h-[190px] overflow-y-auto pr-1.5">
              <div className="flex items-center justify-between"><span className={LABEL}>Section performance</span><span className="text-[11px] text-text-secondary">score · weighted contribution</span></div>
              {sectionScores === undefined ? (
                <p className="text-xs text-text-secondary">Loading section scores…</p>
              ) : sectionScores.length === 0 ? (
                <p className="text-xs text-text-secondary">No section scores recorded.</p>
              ) : sectionScores.map((s) => {
                const pctNum = Number(s.score_pct);
                const wc = Number(s.weighted_contribution);
                return (
                  <div key={s.id} className="flex items-center gap-3">
                    <span className="flex-[0_0_170px] text-xs text-text-primary truncate">{s.section_name}</span>
                    <span className="flex-1 h-2.5 rounded bg-cert-neutral-bg overflow-hidden">
                      <span className={`block h-full rounded ${SECTION_STATUS_BAR[s.status] ?? 'bg-severity-low'}`} style={{ width: `${Math.max(0, Math.min(pctNum, 100))}%` }} />
                    </span>
                    <span className="flex-[0_0_40px] text-xs font-mono text-text-primary text-right">{Number.isFinite(pctNum) ? `${Math.round(pctNum)}%` : '—'}</span>
                    <span className="flex-[0_0_58px] text-[11px] font-mono text-text-secondary text-right">{Number.isFinite(wc) ? `${wc.toFixed(1)} pts` : '—'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TERMINAL (cancelled / expired) ── */}
        {variant === 'terminal' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6 text-center">
            <span className="text-sm font-semibold text-text-secondary">This assessment was {assessment.status}.</span>
            <span className="text-xs text-text-secondary max-w-[52ch] leading-relaxed">
              It is no longer active, so there is no progress or score to show. {d.total} question{d.total !== 1 ? 's' : ''} across {d.sections} section{d.sections !== 1 ? 's' : ''}.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
